import { AuthFetchWithResponse } from 'src/Types/api'
import { ApiResult } from 'src/Libs/api'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { handleFetchError } from 'src/Libs/api/utils'
import { VehicleInformationDto, TargetSocDto, UserActionRequiredErrorDto } from '../dto'
import { VehicleInformationModel } from '../models'
import { transformVehicleInformationDtoToModel } from '../transformer'

/**
 * fetch vehicle information about the user vehicle
 * @param vehicleId 'id' of vehicle in VehicleInformationModel
 * @param authFetch
 */
export const fetchVehicleInformation = async (
  installationId: number | undefined,
  vehicleId: VehicleInformationModel['id'] | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<VehicleInformationModel, AuthFetchError | Error>> => {
  try {
    if (installationId === undefined || vehicleId === undefined) {
      return new ApiResult<VehicleInformationModel, Error | AuthFetchError>(null, true, new Error('no installation or vehicle id was given'))
    }

    const response = await authFetch<VehicleInformationDto>({
      path: `vehicles/installations/${installationId}/vehicles/${vehicleId}`,
      method: 'GET',
      endpoint: process.env.REACT_APP_VEHICLES_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      const vehicleInformation = transformVehicleInformationDtoToModel(response.data)
      return new ApiResult<VehicleInformationModel, Error | AuthFetchError>(vehicleInformation)
    }

    return new ApiResult<VehicleInformationModel, Error | AuthFetchError>(null, true)
  } catch (error) {
    return handleFetchError<VehicleInformationModel>(error)
  }
}

export const updateTargetSoc = async (
  vehicleId: VehicleInformationModel['id'],
  installationId: number,
  targetSoc: TargetSocDto['targetSoC'],
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<TargetSocDto, AuthFetchError | Error>> => {
  try {
    const response = await authFetch<TargetSocDto>({
      path: `vehicles/installations/${installationId}/vehicles/${vehicleId}/soc`,
      method: 'POST',
      payload: { targetSoC: targetSoc },
      endpoint: process.env.REACT_APP_VEHICLES_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      const updatedChargeLimitInfo = response.data
      return new ApiResult<TargetSocDto, Error | AuthFetchError>(updatedChargeLimitInfo)
    }

    return new ApiResult<TargetSocDto, Error | AuthFetchError>(null, true)
  } catch (error) {
    return handleFetchError<TargetSocDto>(error)
  }
}

export const fetchTargetSoc = async (
  vehicleId: VehicleInformationModel['id'],
  installationId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<TargetSocDto, AuthFetchError | Error>> => {
  try {
    const response = await authFetch<TargetSocDto>({
      path: `vehicles/installations/${installationId}/vehicles/${vehicleId}/soc`,
      method: 'GET',
      endpoint: process.env.REACT_APP_VEHICLES_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      const targetSocInformation = response.data
      return new ApiResult<TargetSocDto, Error | AuthFetchError>(targetSocInformation)
    }

    return new ApiResult<TargetSocDto, Error | AuthFetchError>(null, true)
  } catch (error) {
    return handleFetchError<TargetSocDto>(error)
  }
}

/**
 * fetch errors about the user vehicle that require user action
 * eg. 'Location not shared, please enable location sharing in BMW app'
 */
export const fetchVehicleError = async (
  installationId: number | undefined,
  vehicleId: VehicleInformationModel['id'] | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<UserActionRequiredErrorDto[], AuthFetchError | Error>> => {
  try {
    if (installationId === undefined || vehicleId === undefined) {
      return new ApiResult<UserActionRequiredErrorDto[], Error | AuthFetchError>(null, true, new Error('no installation or vehicle id was given'))
    }

    const response = await authFetch<UserActionRequiredErrorDto[]>({
      path: `vehicles/installations/${installationId}/vehicles/${vehicleId}/errors`,
      method: 'GET',
      endpoint: process.env.REACT_APP_VEHICLES_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      const vehicleError = response.data
      return new ApiResult<UserActionRequiredErrorDto[], Error | AuthFetchError>(vehicleError)
    }

    return new ApiResult<UserActionRequiredErrorDto[], Error | AuthFetchError>(null, true)
  } catch (error) {
    return handleFetchError<UserActionRequiredErrorDto[]>(error)
  }
}
